import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  Shield, Users, Building, Key, Plus, Search, 
  Edit, Trash2, MoreVertical, Check, X, AlertCircle
} from 'lucide-react';

const AccessControl = () => {
  const { currentUser } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showNewPermissionForm, setShowNewPermissionForm] = useState(false);

  const [newPermission, setNewPermission] = useState({
    name: '',
    description: '',
    type: 'feature',
    businessUnitId: '',
    roleId: '',
    enabled: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch permissions, business units, and roles in parallel
      const [permissionsRes, businessUnitsRes, rolesRes] = await Promise.all([
        fetch('/api/client-portal/permissions', { headers }),
        fetch('/api/business-units', { headers }),
        fetch('/api/roles', { headers })
      ]);

      if (permissionsRes.ok) {
        const data = await permissionsRes.json();
        setPermissions(data.permissions || []);
      } else {
        // Mock data for demo
        setPermissions([
          {
            id: 1,
            name: 'Dashboard Access',
            description: 'Allow access to client dashboard',
            type: 'feature',
            businessUnitName: 'All Business Units',
            roleName: 'Client User',
            enabled: true,
            userCount: 45
          },
          {
            id: 2,
            name: 'File Upload',
            description: 'Allow file uploads to file share',
            type: 'feature',
            businessUnitName: 'Marketing',
            roleName: 'Client User',
            enabled: true,
            userCount: 12
          },
          {
            id: 3,
            name: 'Ticket Creation',
            description: 'Allow creating support tickets',
            type: 'feature',
            businessUnitName: 'All Business Units',
            roleName: 'Client User',
            enabled: true,
            userCount: 45
          },
          {
            id: 4,
            name: 'Advanced Analytics',
            description: 'Access to detailed analytics dashboard',
            type: 'feature',
            businessUnitName: 'Operations',
            roleName: 'Client Admin',
            enabled: false,
            userCount: 3
          }
        ]);
      }

      if (businessUnitsRes.ok) {
        const data = await businessUnitsRes.json();
        setBusinessUnits(data.businessUnits || []);
      }

      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePermission = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/client-portal/permissions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPermission)
      });

      if (response.ok) {
        fetchData();
        setNewPermission({
          name: '',
          description: '',
          type: 'feature',
          businessUnitId: '',
          roleId: '',
          enabled: true
        });
        setShowNewPermissionForm(false);
      }
    } catch (error) {
      console.error('Error creating permission:', error);
    }
  };

  const togglePermission = async (permissionId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/client-portal/permissions/${permissionId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled })
      });

      if (response.ok) {
        setPermissions(prev => 
          prev.map(p => p.id === permissionId ? { ...p, enabled } : p)
        );
      }
    } catch (error) {
      console.error('Error toggling permission:', error);
    }
  };

  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission.businessUnitName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'enabled' && permission.enabled) ||
                         (filterType === 'disabled' && !permission.enabled) ||
                         (filterType === 'feature' && permission.type === 'feature') ||
                         (filterType === 'data' && permission.type === 'data');
    
    return matchesSearch && matchesFilter;
  });

  const permissionTypes = [
    { id: 'feature', name: 'Feature Access', description: 'Control access to portal features' },
    { id: 'data', name: 'Data Access', description: 'Control access to specific data' },
    { id: 'admin', name: 'Administrative', description: 'Administrative permissions' }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-20 bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Access Control</h1>
          <p className="text-slate-400 mt-1">Manage permissions and access controls for client portals</p>
        </div>
        <button
          onClick={() => setShowNewPermissionForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Permission
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Permissions</p>
              <p className="text-xl font-bold text-slate-100">{permissions.length}</p>
            </div>
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active Permissions</p>
              <p className="text-xl font-bold text-slate-100">
                {permissions.filter(p => p.enabled).length}
              </p>
            </div>
            <Check className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Affected Users</p>
              <p className="text-xl font-bold text-slate-100">
                {permissions.reduce((sum, p) => sum + (p.userCount || 0), 0)}
              </p>
            </div>
            <Users className="w-8 h-8 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Business Units</p>
              <p className="text-xl font-bold text-slate-100">{businessUnits.length}</p>
            </div>
            <Building className="w-8 h-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search permissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-slate-700 text-slate-200 border border-slate-600 rounded-lg px-3 py-2"
          >
            <option value="all">All Permissions</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
            <option value="feature">Feature Access</option>
            <option value="data">Data Access</option>
          </select>
        </div>
      </div>

      {/* Permissions List */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-4 px-6 text-slate-300 font-medium">Permission</th>
                <th className="text-left py-4 px-6 text-slate-300 font-medium">Type</th>
                <th className="text-left py-4 px-6 text-slate-300 font-medium">Business Unit</th>
                <th className="text-left py-4 px-6 text-slate-300 font-medium">Role</th>
                <th className="text-left py-4 px-6 text-slate-300 font-medium">Users</th>
                <th className="text-left py-4 px-6 text-slate-300 font-medium">Status</th>
                <th className="text-right py-4 px-6 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-300 mb-2">No permissions found</h3>
                    <p className="text-slate-400">Create the first permission to control portal access</p>
                  </td>
                </tr>
              ) : (
                filteredPermissions.map(permission => (
                  <tr key={permission.id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                    <td className="py-4 px-6">
                      <div>
                        <div className="text-slate-200 font-medium">{permission.name}</div>
                        <div className="text-slate-400 text-sm">{permission.description}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        permission.type === 'feature' ? 'bg-blue-900/30 text-blue-400' :
                        permission.type === 'data' ? 'bg-purple-900/30 text-purple-400' :
                        'bg-yellow-900/30 text-yellow-400'
                      }`}>
                        {permission.type.charAt(0).toUpperCase() + permission.type.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-slate-300">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-400" />
                        {permission.businessUnitName || 'All Business Units'}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-slate-300">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-slate-400" />
                        {permission.roleName || 'Client User'}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-300">{permission.userCount || 0}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={permission.enabled}
                          onChange={(e) => togglePermission(permission.id, e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-12 h-6 rounded-full transition-colors ${
                          permission.enabled ? 'bg-green-600' : 'bg-slate-600'
                        }`}>
                          <div className={`w-5 h-5 bg-white rounded-full transition-transform transform ${
                            permission.enabled ? 'translate-x-6' : 'translate-x-0.5'
                          } mt-0.5`}></div>
                        </div>
                      </label>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        <button className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-700 rounded">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-slate-400 hover:text-slate-300 hover:bg-slate-700 rounded">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Permission Types Info */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {permissionTypes.map(type => (
          <div key={type.id} className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-5 h-5 text-blue-400" />
              <h3 className="text-slate-200 font-medium">{type.name}</h3>
            </div>
            <p className="text-slate-400 text-sm">{type.description}</p>
            <div className="mt-2">
              <span className="text-slate-300 text-sm font-medium">
                {permissions.filter(p => p.type === type.id).length} permissions
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* New Permission Modal */}
      {showNewPermissionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-lg mx-4">
            <h2 className="text-2xl font-bold text-slate-100 mb-4">Create New Permission</h2>
            
            <form onSubmit={handleCreatePermission}>
              <div className="mb-4">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Permission Name *
                </label>
                <input
                  type="text"
                  value={newPermission.name}
                  onChange={(e) => setNewPermission({...newPermission, name: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder="e.g., Advanced Dashboard Access"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={newPermission.description}
                  onChange={(e) => setNewPermission({...newPermission, description: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 h-20"
                  placeholder="Describe what this permission controls..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Permission Type *
                  </label>
                  <select
                    value={newPermission.type}
                    onChange={(e) => setNewPermission({...newPermission, type: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    required
                  >
                    <option value="feature">Feature Access</option>
                    <option value="data">Data Access</option>
                    <option value="admin">Administrative</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Business Unit
                  </label>
                  <select
                    value={newPermission.businessUnitId}
                    onChange={(e) => setNewPermission({...newPermission, businessUnitId: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  >
                    <option value="">All Business Units</option>
                    {businessUnits.map(bu => (
                      <option key={bu.id} value={bu.id}>{bu.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center justify-between">
                  <span className="text-slate-300 font-medium">Enable Permission</span>
                  <input
                    type="checkbox"
                    checked={newPermission.enabled}
                    onChange={(e) => setNewPermission({...newPermission, enabled: e.target.checked})}
                    className="toggle toggle-primary"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowNewPermissionForm(false)}
                  className="px-4 py-2 text-slate-400 hover:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Create Permission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControl;